import React, { useState, useEffect } from 'react';
import { Muse, MuseTier, MUSE_UPGRADE_COSTS, MuseUpgradePreview } from '../../../types';
import { StarService } from '../../stars/services/StarService';
import { PACKS } from '../../../data/packs';
import '../styles/MuseUpgradeModal.css';
import { playSound } from '../../../utils/audio';

interface MuseUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  muse: Muse;
  userStars: number;
  onUpgradeComplete: (upgradedMuse: Muse, starsCost: number) => void;
  userId?: string;
}

// Helper function to find the pack muse data for images
const findPackMuse = (museName: string) => {
  for (const pack of Object.values(PACKS)) {
    const packMuse = pack.muses.find(m => m.name === museName);
    if (packMuse) return packMuse;
  }
  return null;
};

const MuseUpgradeModal: React.FC<MuseUpgradeModalProps> = ({
  isOpen,
  onClose,
  muse,
  userStars,
  onUpgradeComplete,
  userId
}) => {
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [upgradePreview, setUpgradePreview] = useState<MuseUpgradePreview | null>(null);

  useEffect(() => {
    if (isOpen && muse) {
      calculateUpgradePreview();
    }
  }, [isOpen, muse, userStars]);

  // ESC key to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isUpgrading) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isUpgrading, onClose]);

  const calculateUpgradePreview = () => {
    const currentTier = (muse.tier || 1) as MuseTier;

    if (currentTier >= 3) {
      setUpgradePreview({
        currentTier,
        nextTier: currentTier,
        starsCost: 0,
        canAfford: false,
        tierName: 'Max Tier',
        tierDescription: 'This muse is already at the highest tier.'
      });
      return;
    }

    const nextTier = (currentTier + 1) as MuseTier;
    const starsCost = nextTier === 2 ? MUSE_UPGRADE_COSTS.TIER_2 : MUSE_UPGRADE_COSTS.TIER_3;
    const canAfford = userStars >= starsCost;
    const tierInfo = getTierInfo(nextTier);

    setUpgradePreview({
      currentTier,
      nextTier,
      starsCost,
      canAfford,
      tierName: tierInfo.name,
      tierDescription: tierInfo.description
    });
  };

  const getTierInfo = (tier: MuseTier) => {
    switch (tier) {
      case 2:
        return {
          name: 'Chrome',
          description: 'An ultra-premium metallic finish that gleams with otherworldly power!'
        };
      case 3:
        return {
          name: 'Shiny',
          description: 'A dazzling, rainbow-shimmering version that sparkles like a rare gem!'
        };
      default:
        return { name: 'Normal', description: 'The standard version' };
    }
  };

  const handleUpgrade = async () => {
    if (!upgradePreview || !upgradePreview.canAfford || isUpgrading) return;

    // Play upgrade SFX immediately on button click so it feels responsive
    try {
      playSound('/audio/badge-drop.mp3', 0.7);
    } catch (e) {
      console.warn('Failed to play upgrade click sound', e);
    }

    setIsUpgrading(true);

    try {
      const result = await StarService.upgradeMuse(
        muse.name,
        upgradePreview.currentTier,
        upgradePreview.nextTier,
        upgradePreview.starsCost,
        userId
      );

      if (result.success) {
        const upgradedMuse: Muse = {
          ...muse,
          tier: upgradePreview.nextTier
        };

        setShowSuccess(true);

        setTimeout(() => {
          onUpgradeComplete(upgradedMuse, upgradePreview.starsCost);
          handleClose();
        }, 2000);
      } else {
        void window.appAlert(result.message || 'Upgrade failed. Please try again.');
        setIsUpgrading(false);
      }
    } catch (error) {
      console.error('Error upgrading muse:', error);
      void window.appAlert('An error occurred during upgrade. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleClose = () => {
    if (isUpgrading) return;
    setIsUpgrading(false);
    setShowSuccess(false);
    setUpgradePreview(null);
    onClose();
  };

  const renderMuseImage = (tier: MuseTier, label: string) => {
    const packMuse = findPackMuse(muse.name);
    const imageSource = packMuse?.img || muse.img;

    return (
      <div className="muse-preview-section">
        <div className="muse-preview-label">{label}</div>
        <div className={`muse-image-container muse-tier-${tier}`}>
          {imageSource ? (
            <img
              src={imageSource}
              alt={muse.name}
              className="muse-image"
              draggable={false}
            />
          ) : (
            <div className="muse-placeholder">
              🖼️
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen || !upgradePreview) return null;

  const isMaxTier = upgradePreview.tierName === 'Max Tier';

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className={`modal-container ${showSuccess ? 'success-state' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {!showSuccess ? (
          <>
            {/* Header */}
            <div className="modal-header">
              <h2 className="modal-title">⭐ Upgrade {muse.name}</h2>
              <button
                className="modal-close"
                onClick={handleClose}
                disabled={isUpgrading}
              >
                ×
              </button>
            </div>

            {/* Muse Preview Section */}
            <div className="preview-container">
              <div className="preview-row">
                {renderMuseImage(upgradePreview.currentTier, 'Current')}

                <div className="upgrade-arrow-section">
                  <div className="arrow">→</div>
                  {!isMaxTier && (
                    <div className="cost-badge">
                      ⭐ {upgradePreview.starsCost}
                    </div>
                  )}
                </div>

                {renderMuseImage(upgradePreview.nextTier, isMaxTier ? 'Max Tier' : upgradePreview.tierName)}
              </div>
            </div>

            {/* Description */}
            <div className="description-section">
              <h3>{isMaxTier ? 'Maxed Out!' : `${upgradePreview.tierName} Version`}</h3>
              <p>{upgradePreview.tierDescription}</p>
            </div>

            {/* User Stats */}
            {!isMaxTier && (
              <div className="stats-section">
                <div className="stars-display">
                  <span className="star-icon">⭐</span>
                  <span className="star-count">{userStars}</span>
                  <span className="star-label">available</span>
                </div>
                {!upgradePreview.canAfford && (
                  <div className="insufficient-notice">
                    Need {upgradePreview.starsCost - userStars} more star{upgradePreview.starsCost - userStars !== 1 ? 's' : ''}!
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                className="cancel-btn"
                onClick={handleClose}
                disabled={isUpgrading}
              >
                Cancel
              </button>

              <button
                className={`upgrade-btn ${!isMaxTier && upgradePreview.canAfford ? 'enabled' : 'disabled'}`}
                onClick={handleUpgrade}
                disabled={isMaxTier || !upgradePreview.canAfford || isUpgrading}
              >
                {isUpgrading ? (
                  <>
                    <span className="spinner"></span>
                    Upgrading...
                  </>
                ) : isMaxTier ? (
                  'Max Tier'
                ) : (
                  <>
                    <span className="btn-star">⭐</span>
                    Upgrade to {upgradePreview.tierName}
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          /* Success State */
          <div className="success-content">
            <div className="success-image">
              {renderMuseImage(upgradePreview.nextTier, '')}
            </div>
            <h2 className="success-title">Upgrade Complete!</h2>
            <p className="success-message">
              <span className="highlight-name">{muse.name}</span> has been upgraded to{' '}
              <span className="highlight-tier">{upgradePreview.tierName}</span> tier!
            </p>
            <div className="success-stats">
              <div className="spent-display">
                <span>⭐ -{upgradePreview.starsCost}</span>
              </div>
              <div className="remaining-display">
                <span>{userStars - upgradePreview.starsCost} stars left</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MuseUpgradeModal;